/**
 * Hook usePhaseAutomations - Detecta automações vinculadas a fases do Kanban CRM
 *
 * RELEASE: 2.0.0 - 2026-05-09 - Migrado para server-side via RPC
 *   `get_deal_phase_automations`. Substitui os 4 SELECTs diretos
 *   (crm_pipeline_phases, flows x2, routing_rules) por uma única chamada
 *   SECURITY DEFINER, eliminando exposição de dados sensíveis no Network.
 * RELEASE: 1.1.0 - 2026-01-26 - Suporte a routing_rules e categoria action
 * RELEASE: 1.0.0 - 2026-01-26 - Versão inicial
 */
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger para depuração
const logger = createLogger('PhaseAutomations');

/**
 * Estrutura de automação vinculada a uma fase
 * 
 * RELEASE: 1.1.0 - 2026-01-26 - Adicionado suporte a routing_rules e categoria action
 */
export interface PhaseAutomation {
  /** ID do fluxo ou regra */
  id: string;
  /** Nome do fluxo ou regra */
  name: string;
  /** Tipo de automação: flow ou routing_rule */
  type: 'flow' | 'routing_rule';
  /** Tipo de gatilho (crm_filter, Mover Deal, assign_phase, etc) */
  trigger_type: string;
  /** Status da automação */
  status: 'active' | 'inactive';
  /** Data de criação */
  created_at: string;
  /** URL para edição */
  edit_url: string;
  /** Categoria: trigger (dispara quando entra) ou action (move para a fase) */
  category: 'trigger' | 'action';
}

/**
 * Configuração do gatilho crm_filter extraída do trigger_config
 */
interface CrmFilterTriggerConfig {
  type?: string;
  entity_type?: 'deal' | 'conversation';
  pipeline_id?: string;
  phase_ids?: string[];
}

/**
 * Mapa de automações agrupadas por fase_id
 */
export type PhaseAutomationsMap = Record<string, PhaseAutomation[]>;

/**
 * Hook para detectar automações vinculadas a fases do Kanban CRM
 *
 * Toda a agregação ocorre no servidor via RPC `get_deal_phase_automations`,
 * que retorna o `PhaseAutomationsMap` já no shape esperado pelo frontend.
 *
 * @param pipelineId - ID do pipeline para buscar automações
 */
export function usePhaseAutomations(pipelineId: string | null) {
  const { currentTenant } = useAuth();

  const { data: automationsByPhase = {}, isLoading } = useQuery({
    queryKey: ['phase-automations', pipelineId, currentTenant?.id],
    queryFn: async (): Promise<PhaseAutomationsMap> => {
      if (!pipelineId || !currentTenant?.id) return {};

      logger.debug(`Buscando automações (server-side) para pipeline ${pipelineId}`);

      // Chamada única server-side: validação de tenant + agregação completa
      const { data, error } = await supabase.rpc('get_deal_phase_automations', {
        p_pipeline_id: pipelineId,
        p_tenant_hint: currentTenant.id,
      });

      if (error) {
        logger.error('Erro ao buscar automações via RPC:', error);
        return {};
      }

      // O JSONB retornado já está no shape PhaseAutomationsMap
      const result = ((data as unknown) as PhaseAutomationsMap) || {};

      const totalAutomations = Object.values(result).reduce(
        (sum, arr) => sum + arr.length,
        0,
      );
      logger.debug(
        `RPC retornou ${totalAutomations} automações em ${Object.keys(result).length} fases`,
      );

      return result;
    },
    enabled: !!pipelineId && !!currentTenant?.id,
    staleTime: 60 * 1000, // 1 minuto - automações não mudam frequentemente
    gcTime: 5 * 60 * 1000, // 5 minutos
  });

  // Helper para verificação rápida se fase tem automação
  const hasAutomation = useCallback((phaseId: string): boolean => {
    return (automationsByPhase[phaseId]?.length || 0) > 0;
  }, [automationsByPhase]);

  // Helper para contar automações de uma fase
  const automationCount = useCallback((phaseId: string): number => {
    return automationsByPhase[phaseId]?.length || 0;
  }, [automationsByPhase]);

  // Set de fases com automação (para otimização de renderização)
  const phasesWithAutomation = useMemo(() => {
    return new Set(Object.keys(automationsByPhase));
  }, [automationsByPhase]);

  // Total de automações no pipeline
  const totalAutomations = useMemo(() => {
    return Object.values(automationsByPhase).reduce((sum, arr) => sum + arr.length, 0);
  }, [automationsByPhase]);

  // =========================================================================
  // FASE 2: Helpers para separar automações por categoria
  // =========================================================================
  
  /** Retorna apenas automações do tipo "trigger" (disparam quando entra na fase) */
  const getTriggerAutomations = useCallback((phaseId: string): PhaseAutomation[] => {
    return (automationsByPhase[phaseId] || []).filter(a => a.category === 'trigger');
  }, [automationsByPhase]);

  /** Retorna apenas automações do tipo "action" (movem PARA a fase) */
  const getActionAutomations = useCallback((phaseId: string): PhaseAutomation[] => {
    return (automationsByPhase[phaseId] || []).filter(a => a.category === 'action');
  }, [automationsByPhase]);

  return {
    /** Mapa de automações agrupadas por phase_id */
    automationsByPhase,
    /** Verifica se uma fase específica tem automação */
    hasAutomation,
    /** Conta quantas automações uma fase tem */
    automationCount,
    /** Set de phase_ids que têm automação */
    phasesWithAutomation,
    /** Total de automações no pipeline */
    totalAutomations,
    /** Indica se está carregando */
    isLoading,
    /** Retorna automações do tipo trigger para uma fase */
    getTriggerAutomations,
    /** Retorna automações do tipo action para uma fase */
    getActionAutomations,
  };
}
